import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { NotificationData, NotificationType, NotificationPriority, NotificationSettings, NotificationContextType } from '../types/notification';
import { notificationService } from '../services/notificationService';
import { soundService } from '../services/soundService';

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Default notification settings
export const defaultSettings: NotificationSettings = {
  soundEnabled: true,
  emailNotifications: true,
  pushNotifications: true,
  notificationTypes: {
    [NotificationType.BOOKING]: true,
    [NotificationType.PAYMENT]: true,
    [NotificationType.FLEET]: true,
    [NotificationType.SYSTEM]: true,
    [NotificationType.USER]: true,
    [NotificationType.BLOG]: true,
    [NotificationType.MAINTENANCE]: true,
    [NotificationType.SECURITY]: true,
  }
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [settings, setSettings] = useState<NotificationSettings>(() => {
    const saved = localStorage.getItem('notificationSettings');
    return saved ? JSON.parse(saved) : defaultSettings;
  });

  // Calculate unread count
  const unreadCount = notifications.filter(n => !n.isRead).length;

  // Save settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('notificationSettings', JSON.stringify(settings));
    // Update sound service configuration
    soundService.updateConfig({
      enabled: settings.soundEnabled,
      types: settings.notificationTypes
    });
  }, [settings]);

  // Load initial notifications
  const loadNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await notificationService.getNotifications();
      setNotifications(response.notifications);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle new real-time notification
  const handleNewNotification = useCallback((notification: NotificationData) => {
    setNotifications(prev => [notification, ...prev]);
    
    // Play sound if enabled and type is enabled
    if (settings.soundEnabled && settings.notificationTypes[notification.type]) {
      soundService.playNotificationSound(notification.type, notification.priority);
    }

    // Show browser notification if supported and enabled
    if (settings.pushNotifications && 'Notification' in window && Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/favicon.ico',
        tag: notification.id
      });
    }
  }, [settings.soundEnabled, settings.notificationTypes, settings.pushNotifications]);

  // Initialize real-time updates
  useEffect(() => {
    loadNotifications();

    // Start real-time updates
    notificationService.startRealTimeUpdates(
      handleNewNotification,
      (error) => {
        console.error('Real-time notification error:', error);
      }
    );

    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    return () => {
      notificationService.stopRealTimeUpdates();
    };
  }, [loadNotifications, handleNewNotification]);

  // Play notification sound manually
  const playNotificationSound = useCallback(async () => {
    await soundService.testSound(NotificationType.SYSTEM, NotificationPriority.MEDIUM);
  }, []);

  // Add new notification
  const addNotification = useCallback(async (notificationData: Omit<NotificationData, 'id' | 'createdAt' | 'isRead'>) => {
    try {
      const newNotification = await notificationService.createNotification(notificationData);
      if (newNotification) {
        setNotifications(prev => [newNotification, ...prev]);
        
        // Play sound for manually added notifications
        if (settings.soundEnabled && settings.notificationTypes[notificationData.type]) {
          soundService.playNotificationSound(notificationData.type, notificationData.priority);
        }
      }
    } catch (error) {
      console.error('Failed to add notification:', error);
    }
  }, [settings.soundEnabled, settings.notificationTypes]);

  // Mark notification as read
  const markAsRead = useCallback(async (id: string) => {
    try {
      const success = await notificationService.markAsRead(id);
      if (success) {
        setNotifications(prev => 
          prev.map(notification => 
            notification.id === id 
              ? { ...notification, isRead: true }
              : notification
          )
        );
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      const success = await notificationService.markAllAsRead();
      if (success) {
        setNotifications(prev => 
          prev.map(notification => ({ ...notification, isRead: true }))
        );
      }
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }, []);

  // Delete notification
  const deleteNotification = useCallback(async (id: string) => {
    try {
      const success = await notificationService.deleteNotification(id);
      if (success) {
        setNotifications(prev => prev.filter(notification => notification.id !== id));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  }, []);

  // Clear all notifications
  const clearAllNotifications = useCallback(async () => {
    try {
      const success = await notificationService.clearAllNotifications();
      if (success) {
        setNotifications([]);
      }
    } catch (error) {
      console.error('Failed to clear all notifications:', error);
    }
  }, []);

  // Update settings
  const updateSettings = useCallback((newSettings: Partial<NotificationSettings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  }, []);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    isLoading,
    settings,
    addNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    updateSettings,
    playNotificationSound
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};